/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { FilterDesktop } from '@/components/Filter/FilterDesktop';
import { pageTypes } from '@/constants/page';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import { useLabels } from '@/hooks/useLabels';
import type { ShelfLeftNav } from '@/types/legacy';
import { usePageType } from '@/hooks/usePageType';
import { type HandleFilterType } from '@/types/filterTypes';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { useDeviceType } from '@/utils/useDeviceType';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { getBotStorePickupFilterData } from '@/features/shelf/utils/getBotStorePickupFilterData';

const labelMap = {
  brandShelfSubBrands: 'label_brand_shelf_sub_brands',
  storePickup: 'label_store_pickup',
};

export type Props = {
  content: ShelfLeftNav;
  onFilterSelect?: (a: HandleFilterType) => void;
};

const CMSShelfLeftNav = ({ content, onFilterSelect }: Props) => {
  const { pageType } = usePageType();
  const labels = useLabels(labelMap);
  const legacyRouter = useLegacyRouter();
  const isBot = useDeviceType() === 'bot';
  const isBrandShelf = pageType === pageTypes.BrandShelf;
  const isMerchShelf = pageType === pageTypes.MerchShelf;
  const { acesEnabled } = useAcesFlags();
  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;
  const { data: brandProductData } = useBrandProductDataFunction({ enabled: isBrandShelf });

  const filterNav = content.find((item) => item['@type'] === 'ShelfLeftNav_FilterNav') as
    | ShelfLeftNav[0]
    | undefined;

  const dynamicFilterData = filterNav?.dynamicNavigation?.attributeNarrowingList;

  let staticFilterData;
  staticFilterData = brandProductData.staticNavigation?.map((filter) => {
    const filterData = { ...filter };
    if (filter.dimensionName === 'brand_name_fltr') {
      filterData.displayName = labels.brandShelfSubBrands;
    }
    return filterData;
  });

  staticFilterData = isBrandShelf
    ? staticFilterData
    : isMerchShelf
      ? filterNav?.staticNavigation
      : getBotStorePickupFilterData({
          staticFilterData: filterNav?.staticNavigation,
          isBot,
          shelfPath: legacyRouter.asPath,
          storePickupLabel: labels.storePickup,
        });

  const filterData = { staticFilterData, dynamicFilterData };

  return (
    <FilterDesktop
      staticFilterData={filterData.staticFilterData}
      dynamicFilterData={filterData.dynamicFilterData}
      pageType={pageType ?? pageTypes.productShelf}
      onFilterSelect={onFilterSelect}
    />
  );
};

export { CMSShelfLeftNav };

export default CMSShelfLeftNav;
